import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Drawer, Avatar, Dropdown } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  SettingOutlined,
  MenuOutlined,
  BulbOutlined,
  CloseOutlined,
  LogoutOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckOutlined,
  UserOutlined,
  PartitionOutlined,
  CalendarOutlined,
  FunnelPlotOutlined,
  CrownOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
  MailOutlined,
  FileDoneOutlined,
  RobotOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions, SCOPES, ACTIONS } from '../contexts/PermissionsContext';
import NotificationBell from '../components/NotificationBell';
import BottomNavigation from '../components/BottomNavigation';
import MobileQuickActions from '../components/MobileQuickActions/MobileQuickActions';
import TenantSelector from '../components/TenantSelector';

// Stili separati
import {
  drawerStyles,
  drawerContentStyles,
  mobileNavStyles,
  langMenuStyles,
  mergeStyles,
} from './MainLayout.styles';
import './MainLayout.css';

// Mappa ruoli per visualizzazione
const ROLE_NAMES: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Amministratore',
  sales_manager: 'Responsabile Commerciale',
  senior_sales: 'Commerciale Senior',
  sales: 'Commerciale',
  estimator: 'Preventivista',
  sales_assistant: 'Back Office',
  finance: 'Amministrazione',
  viewer: 'Lettore',
};

const getRoleName = (role?: string): string => {
  if (!role) return '';
  return ROLE_NAMES[role] || role;
};

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, logout, isPlatformAdmin, currentTenant, tenantSettings } = useAuth();
  const { hasPermission } = usePermissions();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const mobile = window.innerWidth < 992;
        setIsMobile(mobile);
        if (!mobile) setMobileOpen(false);
      }, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangMenuOpen(false);
  }, [location.pathname]);

  const isFullscreenPage =
    location.pathname === '/quotes/new' ||
    location.pathname.match(/^\/quotes\/\d+\/edit$/) ||
    location.pathname.match(/^\/order-confirmations\/\d+$/);

  // Verifica se siamo nella sezione Platform Admin
  const isAdminSection = location.pathname.startsWith('/admin');

  // Mappa titoli pagine per mobile header
  const getPageTitle = (): string => {
    const path = location.pathname;

    // Pagine principali
    if (path === '/dashboard') return t('nav.dashboard');
    if (path === '/clients' || path.startsWith('/clients/')) return t('nav.clients');
    if (path === '/quotes' || path.startsWith('/quotes/')) return t('nav.quotes');
    if (path === '/products' || path.startsWith('/products/')) return t('nav.products');
    if (path === '/settings' || path.startsWith('/settings/')) return t('nav.settings');
    if (path === '/appointments' || path.startsWith('/appointments/')) return 'Appuntamenti';
    if (path === '/opportunities' || path.startsWith('/opportunities/')) return 'Pipeline';
    if (path === '/option-groups' || path.startsWith('/option-groups/')) return 'Gruppi Opzioni';
    if (path === '/users/hierarchy') return 'Organigramma';
    if (path === '/admin/users') return 'Gestione Utenti';
    if (path === '/team-commerciale' || path.startsWith('/team-commerciale/')) return 'Team Commerciale';
    if (path === '/notifications') return 'Notifiche';
    if (path === '/admin/dashboard') return 'Platform Overview';
    if (path === '/admin/tenants' || path.startsWith('/admin/tenants/')) return 'Gestione Piattaforma';
    if (path === '/admin/audit-logs') return 'Audit Log';
    if (path === '/audit-logs') return 'Audit Log';
    if (path === '/order-confirmations' || path.startsWith('/order-confirmations/')) return 'Conferme d\'Ordine';

    return 'QuotaPro';
  };

  const pageTitle = getPageTitle();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    window.location.reload();
  };

  // Menu items per l'app normale (QuotaPro)
  const appMenuItems = useMemo(() => {
    const items: Array<{ key: string; icon: any; label: string; section?: string }> = [];

    // Dashboard - sempre visibile se autenticato
    if (hasPermission(SCOPES.DASHBOARD, ACTIONS.VIEW)) {
      items.push({ key: '/dashboard', icon: DashboardOutlined, label: t('nav.dashboard') });
    }

    // Clienti
    if (hasPermission(SCOPES.CLIENTS, ACTIONS.VIEW)) {
      items.push({ key: '/clients', icon: TeamOutlined, label: t('nav.clients') });
    }

    // Appuntamenti (visibile a tutti gli utenti autenticati)
    items.push({ key: '/appointments', icon: CalendarOutlined, label: 'Appuntamenti' });

    // Pipeline Opportunita (visibile a tutti gli utenti autenticati)
    items.push({ key: '/opportunities', icon: FunnelPlotOutlined, label: 'Pipeline' });

    // Prodotti
    if (hasPermission(SCOPES.PRODUCTS, ACTIONS.VIEW)) {
      items.push({ key: '/products', icon: ShoppingOutlined, label: t('nav.products') });
    }

    // Fornitori (visibile ad admin)
    if (hasPermission(SCOPES.PRODUCTS, ACTIONS.VIEW)) {
      items.push({ key: '/suppliers', icon: ShopOutlined, label: 'Fornitori' });
    }

    // Gruppi Opzioni (legato a bundles/products)
    if (hasPermission(SCOPES.BUNDLES, ACTIONS.VIEW)) {
      items.push({ key: '/option-groups', icon: BulbOutlined, label: 'Gruppi Opzioni' });
    }

    // Preventivi
    if (hasPermission(SCOPES.QUOTES, ACTIONS.VIEW)) {
      items.push({ key: '/quotes', icon: FileTextOutlined, label: t('nav.quotes') });
    }

    // Conferme d'Ordine
    if (hasPermission(SCOPES.QUOTES, ACTIONS.VIEW)) {
      items.push({ key: '/order-confirmations', icon: FileDoneOutlined, label: 'Conferme d\'Ordine' });
    }

    // Organigramma - visibile a tutti gli utenti autenticati
    // Ogni ruolo vede il proprio scope (admin: tutto, manager: team, altri: proprio ramo)
    items.push({ key: '/users/hierarchy', icon: PartitionOutlined, label: 'Organigramma' });

    // Team Commerciale - gestione commerciali, livelli e regole approvazione
    if (hasPermission(SCOPES.USERS, ACTIONS.VIEW)) {
      items.push({ key: '/team-commerciale', icon: CrownOutlined, label: 'Team Commerciale' });
    }

    // Impostazioni
    if (hasPermission(SCOPES.SETTINGS, ACTIONS.VIEW)) {
      items.push({ key: '/settings', icon: SettingOutlined, label: t('nav.settings') });
    }

    // Audit Log - Tenant Admin (solo se il tenant ha il flag attivo, o se platform admin)
    if (hasPermission(SCOPES.AUDIT_LOG, ACTIONS.VIEW) && (isPlatformAdmin || tenantSettings.audit_logs_visible_to_tenant_admin)) {
      items.push({ key: '/audit-logs', icon: FileSearchOutlined, label: 'Audit Log' });
    }

    // Gestione Piattaforma (solo Platform Admin)
    if (isPlatformAdmin) {
      items.push({ key: '/admin/dashboard', icon: CrownOutlined, label: 'Gestione Piattaforma', section: 'admin' });
    }

    return items;
  }, [hasPermission, t, isPlatformAdmin, currentTenant, tenantSettings]);

  // Menu items per la sezione Platform Admin
  const adminMenuItems = useMemo(() => {
    const items: Array<{ key: string; icon: any; label: string; section?: string }> = [];

    // Torna all'app
    items.push({ key: '/dashboard', icon: ArrowLeftOutlined, label: 'Torna all\'App', section: 'back' });

    // Dashboard Platform Admin
    items.push({ key: '/admin/dashboard', icon: DashboardOutlined, label: 'Dashboard' });

    // Gestione Organizzazioni
    items.push({ key: '/admin/tenants', icon: BankOutlined, label: 'Organizzazioni' });

    // Gestione Utenti
    items.push({ key: '/admin/users', icon: UserOutlined, label: 'Utenti' });

    // Audit Log - Platform Admin
    items.push({ key: '/admin/audit-logs', icon: FileSearchOutlined, label: 'Audit Log' });

    // Gestione AI - Platform Admin
    items.push({ key: '/admin/ai', icon: RobotOutlined, label: 'Gestione AI' });

    // Test Email - Platform Admin
    items.push({ key: '/admin/email-test', icon: MailOutlined, label: 'Test Email' });

    return items;
  }, []);

  // Seleziona il menu corretto in base al contesto
  const menuItems = isAdminSection ? adminMenuItems : appMenuItems;

  const languages = [
    { code: 'it', label: 'Italiano' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Francais' },
  ];

  // Menu utente per desktop
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Il mio Profilo',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'language',
      icon: <GlobalOutlined />,
      label: 'Lingua',
      children: languages.map(l => ({
        key: l.code,
        label: l.label,
        onClick: () => changeLanguage(l.code),
      })),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      danger: true,
      onClick: () => { logout(); navigate('/login'); },
    },
  ];

  // Menu utente per mobile (con info utente in cima)
  const mobileUserMenuItems = [
    {
      key: 'user-info',
      label: (
        <div className="qp-mobile-user-info">
          <span className="qp-mobile-user-info__name">{user?.fullName}</span>
          <span className="qp-mobile-user-info__role">{getRoleName(user?.role)}</span>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    {
      key: 'language',
      icon: <GlobalOutlined />,
      label: 'Lingua',
      children: languages.map(l => ({
        key: l.code,
        label: l.label,
        onClick: () => changeLanguage(l.code),
      })),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      danger: true,
      onClick: () => { logout(); navigate('/login'); },
    },
  ];

  const handleNav = (key: string) => {
    navigate(key);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileOpen(false);
  };

  return (
    <div className="qp-layout">
      {/* ============================================
          DESKTOP SIDEBAR
          ============================================ */}
      {!isMobile && (
        <aside className={`qp-sidebar ${collapsed ? 'qp-sidebar--collapsed' : ''} ${isAdminSection ? 'qp-sidebar--admin' : ''}`}>
          <div className="qp-sidebar-inner">
            {/* Logo */}
            <div className={`qp-logo ${collapsed ? 'qp-logo--collapsed' : ''}`}>
              {isAdminSection ? (
                <>
                  <div className="qp-logo__icon qp-logo__icon--admin">
                    <CrownOutlined />
                  </div>
                  {!collapsed && <span className="qp-logo__text">Platform Admin</span>}
                </>
              ) : (
                <>
                  <div className="qp-logo__icon">Q</div>
                  {!collapsed && <span className="qp-logo__text">QuotaPro</span>}
                </>
              )}
            </div>

            {/* Navigation */}
            <nav className="qp-nav">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                // Verifica se il path corrente corrisponde esattamente a un'altra voce di menu
                const isExactMatchOfAnotherItem = menuItems.some(m => m.key === location.pathname && m.key !== item.key);
                const isActive = item.section !== 'back' && (
                  location.pathname === item.key ||
                  (location.pathname.startsWith(item.key + '/') && !isExactMatchOfAnotherItem)
                );
                const isAdmin = item.section === 'admin';
                const isBackButton = item.section === 'back';
                return (
                  <React.Fragment key={`${item.key}-${index}`}>
                    <button
                      onClick={() => handleNav(item.key)}
                      className={`qp-nav__item ${isActive ? 'qp-nav__item--active' : ''} ${collapsed ? 'qp-nav__item--collapsed' : ''} ${isAdmin ? 'qp-nav__item--admin' : ''} ${isBackButton ? 'qp-nav__item--back' : ''}`}
                    >
                      <Icon className="qp-nav__icon" />
                      {!collapsed && <span className="qp-nav__label">{item.label}</span>}
                    </button>
                    {isBackButton && <div className="qp-nav__divider" />}
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        </aside>
      )}

      {/* ============================================
          MOBILE DRAWER
          Usa stili inline per evitare conflitti Ant Design
          ============================================ */}
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        width={280}
        closable={false}
        className="qp-drawer"
        styles={drawerStyles}
      >
        <div style={drawerContentStyles}>
          {/* Drawer Header */}
          <div className={`qp-drawer__header ${isAdminSection ? 'qp-drawer__header--admin' : ''}`}>
            <div className="qp-drawer__logo">
              {isAdminSection ? (
                <>
                  <div className="qp-logo__icon qp-logo__icon--small qp-logo__icon--admin">
                    <CrownOutlined />
                  </div>
                  <span className="qp-logo__text qp-logo__text--small">Platform Admin</span>
                </>
              ) : (
                <>
                  <div className="qp-logo__icon qp-logo__icon--small">Q</div>
                  <span className="qp-logo__text qp-logo__text--small">QuotaPro</span>
                </>
              )}
            </div>
            <button onClick={() => setMobileOpen(false)} className="qp-drawer__close">
              <CloseOutlined />
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav style={mobileNavStyles.container}>
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              // Verifica se il path corrente corrisponde esattamente a un'altra voce di menu
              const isExactMatchOfAnotherItem = menuItems.some(m => m.key === location.pathname && m.key !== item.key);
              const isActive = item.section !== 'back' && (
                location.pathname === item.key ||
                (location.pathname.startsWith(item.key + '/') && !isExactMatchOfAnotherItem)
              );
              const isAdmin = item.section === 'admin';
              const isBackButton = item.section === 'back';
              // Aggiungi separatore prima della voce admin (solo in app mode)
              const showDivider = isAdmin && index > 0 && !isAdminSection;
              return (
                <React.Fragment key={`mobile-${item.key}-${index}`}>
                  {showDivider && <div style={mobileNavStyles.divider} />}
                  <button
                    onClick={() => handleNav(item.key)}
                    style={mergeStyles(
                      mobileNavStyles.item,
                      isActive && mobileNavStyles.itemActive,
                      isAdmin && !isAdminSection && { color: '#fbbf24' },
                      isBackButton && {
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px dashed rgba(255,255,255,0.2)',
                        marginBottom: '8px'
                      }
                    )}
                  >
                    <Icon style={mergeStyles(
                      mobileNavStyles.icon,
                      isAdmin && !isAdminSection && { color: '#fbbf24' },
                      isBackButton && { color: '#a5b4fc' }
                    )} />
                    <span style={isBackButton ? { color: '#a5b4fc' } : undefined}>{item.label}</span>
                  </button>
                  {isBackButton && <div style={mobileNavStyles.divider} />}
                </React.Fragment>
              );
            })}

            {/* Separatore */}
            <div style={mobileNavStyles.divider} />

            {/* Menu Lingua */}
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              style={mergeStyles(
                langMenuStyles.button,
                langMenuOpen && langMenuStyles.buttonOpen
              )}
            >
              <div style={langMenuStyles.leftContent}>
                <GlobalOutlined style={mobileNavStyles.icon} />
                <span>Lingua</span>
              </div>
              <span style={langMenuStyles.code}>{i18n.language}</span>
            </button>

            {/* Submenu Lingue */}
            {langMenuOpen && (
              <div style={langMenuStyles.submenu}>
                {languages.map((lang) => {
                  const isActive = i18n.language === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      style={mergeStyles(
                        langMenuStyles.option,
                        isActive && langMenuStyles.optionActive
                      )}
                    >
                      <span>{lang.label}</span>
                      {isActive && <CheckOutlined style={langMenuStyles.checkIcon} />}
                    </button>
                  );
                })}
              </div>
            )}
          </nav>

          {/* Drawer Footer */}
          <div className="qp-drawer__footer">
            {/* User Card */}
            <div className="qp-user-card">
              <Avatar size={36} className="qp-user-card__avatar">
                {user?.fullName?.charAt(0) || 'U'}
              </Avatar>
              <div className="qp-user-card__info">
                <span className="qp-user-card__name">{user?.fullName}</span>
                <span className="qp-user-card__role">{getRoleName(user?.role)}</span>
              </div>
            </div>

            {/* Logout Button */}
            <button onClick={handleLogout} className="qp-logout-btn">
              <LogoutOutlined />
              <span>{t('auth.logout')}</span>
            </button>
          </div>
        </div>
      </Drawer>

      {/* ============================================
          MAIN CONTENT AREA
          ============================================ */}
      <div className={`qp-main ${isMobile ? 'qp-main--mobile' : ''} ${collapsed ? 'qp-main--collapsed' : ''}`}>
        {/* Header */}
        <header className="qp-header">
          {/* Desktop: collapse toggle */}
          {!isMobile && (
            <button onClick={() => setCollapsed(!collapsed)} className="qp-header__collapse-btn">
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          )}

          {/* Mobile: Logo + Page Title */}
          {isMobile && (
            <div className="qp-header__mobile-title">
              <div className="qp-logo__icon qp-logo__icon--header">Q</div>
              <span className="qp-header__page-title">{pageTitle}</span>
            </div>
          )}

          <div className="qp-header__spacer" />

          {/* Tenant Selector (desktop) */}
          {!isMobile && <TenantSelector />}

          {/* Notifications Bell */}
          <NotificationBell />

          {/* Desktop: User Menu */}
          {!isMobile && (
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
              <button className="qp-header__user-btn">
                <Avatar size={34} className="qp-user-card__avatar">
                  {user?.fullName?.charAt(0) || 'U'}
                </Avatar>
                <span className="qp-header__user-name">{user?.fullName}</span>
              </button>
            </Dropdown>
          )}

          {/* Mobile: User Avatar Menu */}
          {isMobile && (
            <Dropdown menu={{ items: mobileUserMenuItems }} trigger={['click']} placement="bottomRight">
              <button className="qp-header__avatar-btn">
                <Avatar size={36} className="qp-user-card__avatar">
                  {user?.fullName?.charAt(0) || 'U'}
                </Avatar>
              </button>
            </Dropdown>
          )}
        </header>

        {/* Content */}
        <main className={`qp-content ${isMobile ? 'qp-content--mobile' : ''}`}>
          <div className={`${isFullscreenPage ? 'qp-content__fullscreen' : 'qp-content__wrapper'}`}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile: Bottom Navigation */}
      {isMobile && <BottomNavigation />}

      {/* Mobile: Quick Actions linguetta */}
      {isMobile && <MobileQuickActions />}
    </div>
  );
};

export default MainLayout;